import React, { useContext, useState, useEffect } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { CustomerContext } from "../CashierHomepage";
import { CustomerIdContext } from "../../context/CustomerIdProvider";
import CashierApi from "../../utility/CashierApi";
import { AutoBillContext } from "../../context/AutoBillProvider";
import PaymentMethod from "./PaymentMethod";
import { printReceipt, saveLastReceipt } from "../utils/printReceipt";
import { fetchIsPro } from "../../components/ProGate";
import { cashierAxios } from "../../axiosInstance";
import { notify } from "../../components/toastify";
import { enqueueSale, offlineOrderNo } from "../../utility/offline/saleQueue";
import { resetAfterSale } from "../../utility/offline/offlineCart";
import { isOffline } from "../../utility/offline/reachability";

function Payment({ currency }) {
  const { createData, updateData, fetchOnlyData } = CashierApi();
  const queryClient = useQueryClient();
  const { newCartData, setUpdateStatus, updateResumeStatus, draftId } =
    useContext(CustomerContext);
  const { selectedCustomerId } = useContext(CustomerIdContext);
  const { autoBill } = useContext(AutoBillContext);
  const [, setNewBill] = useState({});
  const [orderData, setOrderData] = useState({});
  const [payMethodModal, setPayMethodModal] = useState(false);

  const [payMethodName, setPayMethodName] = useState("");

  // Calling the payment endpoint
  const { data: paymentType } = useQuery({
    queryKey: ["paymentType"],
    queryFn: () => fetchOnlyData("payment-methods"),
    retry: 1,
    keepPreviousData: true,
    staleTime: 20 * 60 * 1000,
    cacheTime: 20 * 60 * 1000,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });

  const { data: template } = useQuery({
    queryKey: ["template"],
    queryFn: () => fetchOnlyData("receipt-template"),
    retry: 1,
    keepPreviousData: true,
    staleTime: 30 * 60 * 1000,
    cacheTime: 60 * 60 * 1000,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });

  const mutation = useMutation({
    mutationFn: async (data) => {
      if (updateResumeStatus) {
        return updateData(`orders/update-draft/`, draftId, data);
      }
      // Saves the sale on this device; it syncs when the connection returns.
      const saveOffline = () => {
        const sale = enqueueSale(data);
        return {
          offline: true,
          data: {
            order: {
              order_no: offlineOrderNo(sale),
              total_price: data.total,
              payment_type: data.method_name,
            },
          },
        };
      };
      if (isOffline()) return saveOffline();
      try {
        return await createData("orders/store", data);
      } catch (error) {
        if (error?.isNetworkError) return saveOffline();
        throw error;
      }
    },
    retry: false,
    onError: (error) => {
      if (error?.isNetworkError) {
        notify("You're offline. Drafts can't be completed offline.");
      }
    },
    onSuccess: (response) => {
      if (response?.offline) {
        resetAfterSale(selectedCustomerId);
        notify(
          "Sale saved offline. It will sync when the internet returns.",
          "info"
        );
      }
      queryClient.invalidateQueries({ queryKey: ["cart"] });
      generateAndPrintReceipt(response);
      setUpdateStatus(false);
    },
  });

  const generateAndPrintReceipt = (response) => {
    const order = response?.data?.order;
    const now = new Date();

    const receipt = {
      orderNo: order?.order_no || order?.order_number || order?.orderNO,
      date: now.toLocaleDateString(),
      time: now.toLocaleTimeString(),
      items: (newCartData || []).map((item) => ({
        name: item?.product?.product_name,
        quantity: item?.quantity,
        total: item?.total_price,
        categoryId: item?.product?.cat_id ?? item?.cat_id ?? null,
        productId: item?.product_id ?? null,
      })),
      autoBill: (autoBill || []).map((bill) => ({
        name: bill?.bill_name,
        amount: bill?.auto_bill,
      })),
      total: order?.total_price,
      paymentType: order?.payment_type,
    };

    saveLastReceipt(receipt);
    printReceipt({
      receipt,
      template,
      currency,
      kind: "sale",
      // Extra printers are Pro: checked now, at print time.
      checkPro: () => fetchIsPro(queryClient, cashierAxios, "cashier"),
    });
  };

  const handleSubmit = () => {
    // The server rejects an order with no items (400), so don't send one.
    if (!orderData?.carts?.length) {
      notify("Add at least one item to the cart before paying.");
      return;
    }
    if (!paymentType || paymentType.length === 0) {
      mutation.mutate(orderData);
    } else {
      setPayMethodModal(true);
    }
  };

  const handlePaymentMethod = (name) => {
    setPayMethodName(name);
    const updatedOrderData = {
      ...orderData,
      method_name: name,
    };
    setOrderData(updatedOrderData);
    mutation.mutate(updatedOrderData);
    setPayMethodModal(false);
  };

  useEffect(() => {
    setNewBill(autoBill);

    const cartInfo = newCartData?.map((item) => {
      return {
        product_id: Number(item?.product_id),
        price: Number(item?.price),
        quantity: Number(item?.quantity),
      };
    });

    // Calculate subtotal (sum of all cart items)
    const subtotal = newCartData?.reduce((sum, item) => {
      return sum + (Number(item?.price) * Number(item?.quantity));
    }, 0) || 0;

    // Calculate discount and tax amounts from autoBill
    let discountAmount = 0;
    let taxAmount = 0;
    let discountId = null;
    let taxId = null;

    autoBill?.forEach((bill) => {
      const billName = bill?.bill_name?.toLowerCase() || "";
      const billAmount = Number(bill?.auto_bill) || 0;
      
      // Check if it's a discount (you may need to adjust this logic based on your bill names)
      if (billName.includes("discount")) {
        discountAmount += billAmount;
        discountId = bill?.id || null; // Include if you have the ID
      }
      // Check if it's a tax (VAT, tax, etc.)
      else if (billName.includes("vat") || billName.includes("tax")) {
        taxAmount += billAmount;
        taxId = bill?.id || null; // Include if you have the ID
      }
    });

    // Calculate total: subtotal - discount + tax
    const total = subtotal - discountAmount + taxAmount;

    const orderPayload = {
      customer_id: selectedCustomerId,
      status: "completed",
      carts: cartInfo,
      subtotal: Number(subtotal.toFixed(2)),
      discount_amount: Number(discountAmount.toFixed(2)),
      tax_amount: Number(taxAmount.toFixed(2)),
      total: Number(total.toFixed(2)),
      send_option: "now",
      scheduled_date: null,
      scheduled_time: null,
      recurring_frequency: null,
      ...(paymentType?.length > 0 && { method_name: payMethodName }),
      ...(discountId && { discount: discountId }),
      ...(taxId && { tax: taxId }),
    };

    console.log("Order Payload:", orderPayload);
    setOrderData(orderPayload);
  }, [newCartData, selectedCustomerId, payMethodName, autoBill, paymentType?.length]);

  return (
    <>
      <button
        type="submit"
        onClick={() => handleSubmit()}
        className="p-2 my-4 rounded-lg text-white w-full bg-gray-500 hover:bg-gray-600"
        // disabled={newCart?.data?.length < 1} // Uncomment this on confirmation
      >
        Proceed to pay
      </button>
      {payMethodModal && (
        <PaymentMethod
          setPayMethodModal={setPayMethodModal}
          handlePaymentMethod={handlePaymentMethod}
          paymentType={paymentType}
        />
      )}
    </>
  );
}

export default Payment;